import { useEffect } from "react";
import { motion } from "motion/react";

import { type Conversation } from "shared/apiClient";
import { cn, getText, interpolate, type Noticeboard } from "shared/lib";
import { animationVariants } from "./lib";

import { useSkipAnalysisLayoutAnimations } from "@/hooks";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  PublicConversation,
  Separator,
} from "shared/components";
import { Lot } from "./Lot";
import { Proposal } from "./Proposal";
import { MultiLingualDisclaimer } from "@/components/analysisLayout/multiLingualDisclaimer";

export interface LotAndProposalProps {
  conversation: Conversation | undefined | null;
  messages: Conversation["messages"];
  noticeboardData: Noticeboard | undefined | null;
  printable?: boolean;
}

export function LotAndProposal({
  conversation,
  messages,
  noticeboardData,
  printable,
}: LotAndProposalProps) {
  const [skipAnimations, setSkipAnimations] = useSkipAnalysisLayoutAnimations();

  useEffect(() => {
    setTimeout(() => {
      setSkipAnimations(true);
    }, 1000);
  }, [setSkipAnimations]);

  return (
    <div
      className={cn(
        "flex flex-col gap-10 overflow-hidden",
        printable && "gap-5",
      )}
    >
      <motion.div
        className="flex flex-col gap-1 [transform-origin:top_left]"
        variants={animationVariants}
        initial={!skipAnimations && "hidden"}
        animate={["show", "opacity"]}
      >
        <h2 className="text-heading2xl text-textPrimary">
          {getText("Lot and proposal")}
        </h2>

        <p className="text-textSubdued text-bodylg">
          {getText(
            "Planning information about your entered address, and how we've made sense of your proposal based on your responses.",
          )}
        </p>
      </motion.div>

      <div className="overflow-hidden">
        <motion.div
          className={cn(
            "flex flex-col gap-10 [transform-origin:top_left]",
            printable && "gap-5",
          )}
          variants={animationVariants}
          initial={!skipAnimations && "hidden"}
          animate={["show", "opacity"]}
        >
          <div className="empty:hidden" id="content-header-portal" />

          <Lot conversation={conversation} printable={printable} />

          <Separator orientation="horizontal" />

          <Proposal noticeboardData={noticeboardData} />

          <Separator orientation="horizontal" />

          <Accordion
            collapsible
            type="single"
            {...(printable && { collapsible: false, defaultValue: "messages" })}
          >
            <AccordionItem value="messages">
              <AccordionTrigger>
                <div className="flex flex-col items-start">
                  <h3 className="text-headingmd text-textPrimary font-semibold">
                    {getText("Conversation log")}
                  </h3>

                  <p className="text-textSubdued text-bodymd">
                    {getText(
                      "A log of questions you were asked and how you responded.",
                    )}
                  </p>
                </div>
              </AccordionTrigger>

              <AccordionContent>
                <PublicConversation
                  className="bg-interfaceWhite border-interfaceBorder rounded border p-5"
                  conversation={conversation}
                  conversationMessages={messages}
                  isPublic
                  translate={{ getText, interpolate }}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="empty:hidden" id="content-footer-portal" />

          <MultiLingualDisclaimer />
        </motion.div>
      </div>
    </div>
  );
}
